import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Briefcase, MessageCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { generateWhatsAppLink } from '@/utils/whatsappTemplates';

const QuoteBusiness = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const campaign = searchParams.get('campaign') || '';
  
  const [formData, setFormData] = useState({
    businessName: '',
    contactPerson: '',
    email: '',
    mobile: '',
    monthlyVolume: '',
    primaryArea: '',
    businessType: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    sessionStorage.setItem('quoteData', JSON.stringify({
      ...formData,
      serviceType: 'business',
      campaign
    }));
    navigate('/quote/confirm');
  };

  const whatsappLink = generateWhatsAppLink('business', {
    businessName: formData.businessName,
    volume: formData.monthlyVolume,
    area: formData.primaryArea,
    email: formData.email
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Indicator */}
      <div className="bg-white border-b py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center space-x-4 max-w-2xl mx-auto">
            <div className="flex items-center">
              <div className="bg-[#039fff] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">1</div>
              <span className="ml-2 text-sm font-medium text-[#039fff]">Details</span>
            </div>
            <div className="h-px w-12 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="bg-gray-300 text-gray-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">2</div>
              <span className="ml-2 text-sm text-gray-600">Confirm</span>
            </div>
            <div className="h-px w-12 bg-gray-300"></div>
            <div className="flex items-center">
              <div className="bg-gray-300 text-gray-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">3</div>
              <span className="ml-2 text-sm text-gray-600">Done</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="mb-8">
              <Briefcase className="h-12 w-12 text-[#039fff] mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-[#001A47] text-center mb-2">Rocket Van Express Business Account</h1>
              <p className="text-center text-gray-600">Apply for monthly billing and priority service</p>
            </div>

            <Card>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="businessName">Business Name *</Label>
                    <Input
                      id="businessName"
                      value={formData.businessName}
                      onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                      placeholder="Your company name"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contactPerson">Contact Person *</Label>
                      <Input
                        id="contactPerson"
                        value={formData.contactPerson}
                        onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                        placeholder="Full name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="businessType">Type of Business *</Label>
                      <Input
                        id="businessType"
                        value={formData.businessType}
                        onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                        placeholder="e.g., E-commerce, Restaurant"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="business@example.com"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="mobile">Mobile Number *</Label>
                      <Input
                        id="mobile"
                        type="tel"
                        value={formData.mobile}
                        onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                        placeholder="+27"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="monthlyVolume">Estimated Monthly Volume *</Label>
                      <select
                        id="monthlyVolume"
                        value={formData.monthlyVolume}
                        onChange={(e) => setFormData({ ...formData, monthlyVolume: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        required
                      >
                        <option value="">Select...</option>
                        <option value="1-10">1-10 deliveries/month</option>
                        <option value="11-30">11-30 deliveries/month</option>
                        <option value="31+">31+ deliveries/month</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="primaryArea">Primary Service Area *</Label>
                      <Input
                        id="primaryArea"
                        value={formData.primaryArea}
                        onChange={(e) => setFormData({ ...formData, primaryArea: e.target.value })}
                        placeholder="e.g., Stellenbosch & Somerset West"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="notes">Additional Information</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Tell us about your delivery needs..."
                      rows={3}
                    />
                  </div>

                  <div className="bg-[#039fff]/5 p-4 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>What happens next:</strong> We'll review your application and contact you within 24 hours to set up your account.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button type="submit" className="flex-1 bg-[#fb6402] hover:bg-[#e55a02]">
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button type="button" variant="outline" className="w-full">
                        <MessageCircle className="mr-2 h-4 w-4" />
                        Chat on WhatsApp
                      </Button>
                    </a>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuoteBusiness;
